// src/frontend/src/components/Landing/HeroSection.tsx - Enhanced with specific metrics
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const HeroSection: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [currentWord, setCurrentWord] = useState(0);
  const words = ['Difficult', 'Challenging', 'Complex', 'Critical'];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWord((prev) => (prev + 1) % words.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>
      
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />
      
      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
        <div className="text-center">
        {/* Enhanced Badge with specific metrics */}
        <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 backdrop-blur-sm mb-8 relative z-0">
          <span className="text-blue-300 text-sm font-medium">🎯 5,000+ PMs improved stakeholder satisfaction by 28%</span>
        </div>
          
          <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tight">
            <span className="block">Your AI-Powered</span>
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
              PM Coach
            </span>
            <span className="block text-4xl md:text-5xl mt-2 font-normal">
              Master 
              <span className="inline-block transition-all duration-500 ease-in-out transform ml-2">
                {words[currentWord]}
              </span>
              {' '}Conversations
            </span>
          </h1>
          
          <p className="mt-8 max-w-3xl mx-auto text-xl text-gray-300 leading-relaxed">
            Unlike generic coaching platforms, we focus on one thing: helping PMs master 
            stakeholder conversations through realistic AI-powered practice sessions with 
            <strong className="text-white"> real-time coaching tips</strong> and instant feedback.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="group relative inline-flex items-center px-8 py-4 text-lg font-medium rounded-full text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <span>Go to Coaching Dashboard</span>
                <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            ) : (
              <>
                <Link
                  to="/register"
                  className="group relative inline-flex items-center px-8 py-4 text-lg font-medium rounded-full text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <span>Get 20 Free Coaching Sessions</span>
                  <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <a
                  href="#live-demo"
                  className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-full text-white border-2 border-white/20 hover:bg-white/10 backdrop-blur-sm transition-all duration-200"
                >
                  <svg className="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Watch Coaching Demo
                </a>
              </>
            )}
          </div>

          {/* Enhanced trust indicators with specific metrics */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '28%', label: 'Improvement in stakeholder satisfaction', sublabel: 'Fortune 500 teams' },
              { number: '2.3x', label: 'Faster skill development vs traditional training', sublabel: 'Hours not months' },
              { number: '85%', label: 'Report improved confidence in 14 days', sublabel: '4.9/5 rating' },
              { number: '10x', label: 'More affordable than executive coaching', sublabel: '$19-39/month' }
            ].map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="text-3xl font-bold text-white group-hover:text-blue-400 transition-colors">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
                <div className="text-xs text-gray-500 mt-1">{stat.sublabel}</div>
              </div>
            ))}
          </div>
          
          {/* Enhanced trial messaging */}
          <div className="mt-12 bg-white/5 backdrop-blur-sm rounded-lg p-6 max-w-2xl mx-auto border border-white/10">
            <p className="text-blue-100 text-sm mb-2">
              <strong className="text-white">No credit card required</strong> • 14-day free trial • Cancel anytime
            </p>
            <p className="text-xs text-gray-400">
              Join 5,000+ PMs who chose focused practice over generic advice
            </p>
          </div>
        </div>
      </div>

      {/* Animated scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <svg className="w-6 h-6 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>
    </div>
  );
};

export default HeroSection;